/**
 * The one time the app asks for birdie alerts.
 *
 * Twelve men signed in and three of them heard about a birdie. Nothing was
 * broken: every alert that fired reached every phone subscribed to it. But
 * signing in does not subscribe you, and on an iPhone nothing can until the app
 * is on the home screen — Apple grants web push to installed apps only, never
 * to a Safari tab. The control has always existed, correctly worded, on the
 * Phone tab. Nobody goes to the Phone tab.
 *
 * So this asks, once, on a screen men read between shots, and it says the true
 * next step rather than a generic "enable notifications" — which on iOS is not
 * an available action at all and would fail every time it was tapped, teaching
 * everybody that the app is broken.
 *
 * Dismissed is dismissed. A nag that comes back is worse than no nag: it gets
 * swiped past without reading, and then the one message that mattered gets
 * swiped past with it.
 */

import { useState } from 'react'
import type { PushState } from '../push'

export const NUDGE_KEY = 'woodsmen:birdie-nudge-dismissed'

const dismissed = (): boolean => {
  try {
    return localStorage.getItem(NUDGE_KEY) !== null
  } catch {
    // Private mode, or storage full. Showing it is the lesser harm.
    return false
  }
}

export function BirdieNudge({
  state,
  onEnable,
}: {
  state: PushState | null
  /** Runs the real enable flow and reports how it ended. */
  onEnable: () => Promise<PushState>
}) {
  const [gone, setGone] = useState(dismissed)
  /*
   * What happened when he tapped Turn on. 'blocked' is the one that matters:
   * he tapped our button and then told the PHONE no, and the OS will never
   * show that sheet again. Dismissing the nudge at that moment — which is what
   * this used to do — left him with alerts off, the message gone, and nobody
   * to tell him the only ways back. So the nudge now waits for the answer, and
   * on a decline it stays and says the honest recovery: the phone's Settings,
   * or delete the app from the home screen and add it again.
   */
  const [declined, setDeclined] = useState(false)
  const [busy, setBusy] = useState(false)

  if (gone || (state !== 'off' && state !== 'needs-install')) return null

  const install = state === 'needs-install'

  const drop = () => {
    setGone(true)
    try {
      localStorage.setItem(NUDGE_KEY, new Date().toISOString())
    } catch {
      /* it will ask once more next launch, which is survivable */
    }
  }

  const turnOn = async () => {
    setBusy(true)
    try {
      const result = await onEnable()
      if (result === 'on') drop()
      else if (result === 'blocked') setDeclined(true)
      // 'off' means the sheet was swiped away without an answer — the ask
      // survives untouched, and he can tap again.
    } catch {
      /* a network failure is not a decline; leave the ask standing */
    } finally {
      setBusy(false)
    }
  }

  if (declined) {
    return (
      <div className="border-b border-rule bg-wash px-4 py-3">
        <p className="bc-label">Notifications are blocked</p>
        <p className="text-[13px] leading-snug mt-1 text-mute">
          The phone was told no, and it will not let this app ask again. To get birdie alerts,
          either allow them in Settings &rarr; Notifications &rarr; Woodsmen, or delete the app
          from the home screen, add it again, and answer Allow this time — a reinstall resets
          the question.
        </p>
        <div className="flex gap-2 mt-2">
          <button type="button" onClick={drop} className="bc-tap bc-label border-2 border-ink px-4 py-2">
            Got it
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="border-b border-rule bg-wash px-4 py-3">
      <p className="bc-label">Birdie alerts are off</p>
      <p className="text-[13px] leading-snug mt-1 text-mute">
        {install
          ? 'Everyone with them on hears about a birdie or better, about half a minute after it is entered. iPhone only sends these to an app on the home screen — tap Share, then “Add to Home Screen”, and open it from there.'
          : 'Hear about a birdie or better anywhere in the field, about half a minute after it is entered.'}
      </p>
      <div className="flex gap-2 mt-2">
        {!install && (
          <button
            type="button"
            onClick={() => void turnOn()}
            disabled={busy}
            className="bc-tap bc-label bg-signal text-paper px-4 py-2 disabled:opacity-40"
          >
            {busy ? '…' : 'Turn them on'}
          </button>
        )}
        <button type="button" onClick={drop} className="bc-tap bc-label border-2 border-ink px-4 py-2">
          Not now
        </button>
      </div>
    </div>
  )
}
